import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import classes from "./SDGGoals.module.css";
import { getSDGIcon } from "./SDGIcons";
import { BaseURL } from "../home/BaseData";

const SDG_COLORS = {
  1: "#FF7A00",
  2: "#EAB308",
  3: "#22C55E",
  4: "#EF4444",
  5: "#EC4899",
  6: "#3B82F6",
  7: "#F97316",
  8: "#06B6D4",
  9: "#64748B",
  10: "#F59E0B",
  11: "#8B5CF6",
  12: "#84CC16",
  13: "#64748B",
  14: "#0284C7",
  15: "#10B981",
  16: "#F43F5E",
  17: "#14B8A6",
};

const SLIDE_INTERVAL_MS = 7000;

const FALLBACK = {
  title: {
    uz: "Barqaror Rivojlanish Maqsadlari (SDG)",
    en: "Sustainable Development Goals (SDGs)",
    ru: "Цели устойчивого развития (ЦУР)",
  },
  infoTitle: {
    uz: "SDG nima ?",
    en: "What are the SDGs?",
    ru: "Что такое ЦУР?",
  },
  infoDescription: {
    uz: "SDG (Sustainable Development Goals) — bu \"barqaror rivojlanish maqsadlari\" bo'lib, 2015-yilda BMT (Birlashgan Millatlar Tashkiloti) tomonidan qabul qilingan 17 ta global maqsadlar to'plamidir. Ular 2030-yilgacha dunyodagi atrof-muhit, iqtisodiyot va ijtimoiy barqarorlikni ta'minlashga qaratilgan.",
    en: "The SDGs (Sustainable Development Goals) are a set of 17 global goals adopted by the United Nations (UN) in 2015. They aim to ensure environmental, economic, and social sustainability around the world by 2030.",
    ru: "ЦУР (Цели устойчивого развития) — это набор из 17 глобальных целей, принятых Организацией Объединённых Наций (ООН) в 2015 году. Они направлены на обеспечение экологической, экономической и социальной устойчивости в мире до 2030 года.",
  },
  goalsHeader: {
    uz: "Barqaror rivojlanish maqsadlari quyidagilarni o'z ichiga oladi",
    en: "Sustainable Development Goals include",
    ru: "Цели устойчивого развития включают в себя",
  },
  goalsEyebrow: {
    uz: "GLOBAL MAQSADLAR",
    en: "GLOBAL GOALS",
    ru: "ГЛОБАЛЬНЫЕ ЦЕЛИ",
  },
  goalLabel: {
    uz: "MAQSAD",
    en: "GOAL",
    ru: "ЦЕЛЬ",
  },
  prev: { uz: "Oldingi", en: "Previous", ru: "Назад" },
  next: { uz: "Keyingi", en: "Next", ru: "Вперёд" },
  pause: { uz: "To'xtatish", en: "Pause", ru: "Пауза" },
  play: { uz: "Davom ettirish", en: "Play", ru: "Продолжить" },
  map: {
    uz: "Google Xaritalarda ochish",
    en: "Open in Google Maps",
    ru: "Открыть в Google Картах",
  },
  loading: { uz: "Yuklanmoqda...", en: "Loading...", ru: "Загрузка..." },
};

const SDGGoals = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const [sdgInfo, setSdgInfo] = useState(null);
  const [goals, setGoals] = useState([]);
  const [heroSlides, setHeroSlides] = useState([]);
  const [commitment, setCommitment] = useState(null);
  const [initiatives, setInitiatives] = useState([]);
  const [strategy, setStrategy] = useState([]);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  const [slideIndex, setSlideIndex] = useState(0);
  const [slidePaused, setSlidePaused] = useState(false);

  useEffect(() => {
    const fetchSDGData = async () => {
      try {
        const urlsToTry = [
          `${BaseURL.replace(/\/$/, '')}/api/sdg/`,
          "http://127.0.0.1:8001/api/sdg/",
          "http://127.0.0.1:8000/api/sdg/",
          "http://localhost:8000/api/sdg/",
          "http://localhost:8001/api/sdg/"
        ];
        let response = null;
        for (const url of urlsToTry) {
          try {
            response = await axios.get(url);
            if (response && response.data && response.data.goals) break;
          } catch (e) {
            // try next
          }
        }

        if (response && response.data) {
          const data = response.data;
          setSdgInfo(data.info);
          setGoals(data.goals || []);
          // Quyidagi bo'limlar serverda hali bo'lmasligi ham mumkin —
          // shunda ular bo'sh qoladi va saytda umuman chizilmaydi.
          setHeroSlides(data.hero_slides || []);
          setCommitment(data.commitment || null);
          setInitiatives(data.initiatives || []);
          setStrategy(data.strategy || []);
          setContact(data.contact || null);
        }
      } catch (error) {
        console.error("Error fetching SDG data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSDGData();
  }, []);

  const lang = i18n.language || "uz";

  // Tanlangan tilda bo'sh bo'lsa o'zbekchaga qaytadi — bo'sh sarlavha chiqmasin.
  const L = useCallback(
    (obj, prefix) => {
      if (!obj) return "";
      return obj[`${prefix}_${lang}`] || obj[`${prefix}_uz`] || "";
    },
    [lang]
  );

  const T = useCallback((key) => FALLBACK[key][lang] || FALLBACK[key].uz, [lang]);

  const mediaUrl = useCallback((path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    return `${BaseURL.replace(/\/$/, "")}${path.startsWith("/") ? "" : "/"}${path}`;
  }, []);

  const slides = useMemo(
    () => heroSlides.filter((s) => L(s, "title") || s.image),
    [heroSlides, L]
  );

  useEffect(() => {
    if (slides.length < 2 || slidePaused) return undefined;
    const id = setInterval(
      () => setSlideIndex((i) => (i + 1) % slides.length),
      SLIDE_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, [slides.length, slidePaused]);

  useEffect(() => {
    if (slideIndex >= slides.length) setSlideIndex(0);
  }, [slides.length, slideIndex]);

  if (loading) {
    return <div className={classes.loading}>{T("loading")}</div>;
  }

  const infoTitle = L(sdgInfo, "info_title") || T("infoTitle");
  const infoDescription = L(sdgInfo, "info_description") || T("infoDescription");
  const goalsHeader = L(sdgInfo, "goals_header") || T("goalsHeader");
  const pageTitle = L(sdgInfo, "title") || T("title");
  const pageSubtitle = L(sdgInfo, "subtitle");

  const commitmentPoints = (commitment?.points || []).filter((p) => L(p, "text"));

  return (
    <div className={classes.page}>
      {/* ---------------- Hero slayder ---------------- */}
      {slides.length > 0 && (
        <section className={classes.hero} aria-label={pageTitle}>
          {slides.map((slide, idx) => {
            const img = mediaUrl(slide.image);
            return (
              <div
                key={slide.id}
                className={`${classes.heroSlide} ${idx === slideIndex ? classes.heroSlideActive : ""}`}
                aria-hidden={idx !== slideIndex}
              >
                {img && <img src={img} alt="" className={classes.heroImg} />}
                <div className={classes.heroScrim} />
                <div className={classes.heroInner}>
                  {L(slide, "eyebrow") && (
                    <span className={classes.heroEyebrow}>{L(slide, "eyebrow")}</span>
                  )}
                  <h1 className={classes.heroTitle}>{L(slide, "title")}</h1>
                  {L(slide, "description") && (
                    <p className={classes.heroText}>{L(slide, "description")}</p>
                  )}
                  {slide.button_url && L(slide, "button_text") && (
                    <a className={classes.heroBtn} href={slide.button_url}>
                      {L(slide, "button_text")}
                      <span aria-hidden="true"> →</span>
                    </a>
                  )}
                </div>
              </div>
            );
          })}

          {slides.length > 1 && (
            <div className={classes.heroControls}>
              <button
                type="button"
                className={classes.heroCtrlBtn}
                aria-label={T("prev")}
                onClick={() =>
                  setSlideIndex((i) => (i - 1 + slides.length) % slides.length)
                }
              >
                ←
              </button>
              <button
                type="button"
                className={classes.heroCtrlBtn}
                aria-label={T("next")}
                onClick={() => setSlideIndex((i) => (i + 1) % slides.length)}
              >
                →
              </button>
              <button
                type="button"
                className={classes.heroCtrlBtn}
                aria-label={slidePaused ? T("play") : T("pause")}
                onClick={() => setSlidePaused((p) => !p)}
              >
                {slidePaused ? "▶" : "❙❙"}
              </button>
              <span className={classes.heroCounter}>
                {slideIndex + 1} / {slides.length}
              </span>
            </div>
          )}
        </section>
      )}

      {/* ---------------- Sahifa sarlavhasi (slayder bo'lmasa) ---------------- */}
      {slides.length === 0 && (
        <section className={`${classes.section} ${classes.pageHead}`}>
          {pageSubtitle && <span className={classes.eyebrow}>{pageSubtitle}</span>}
          <h1 className={classes.pageTitle}>{pageTitle}</h1>
        </section>
      )}

      {/* ---------------- Majburiyat ---------------- */}
      {commitment && (L(commitment, "title") || L(commitment, "description")) && (
        <section className={`${classes.section} ${classes.commitment}`}>
          <div className={classes.commitmentText}>
            {L(commitment, "eyebrow") && (
              <span className={classes.eyebrow}>{L(commitment, "eyebrow")}</span>
            )}
            {L(commitment, "title") && (
              <h2 className={classes.sectionTitle}>{L(commitment, "title")}</h2>
            )}
            {L(commitment, "description") && (
              <p className={classes.lead}>{L(commitment, "description")}</p>
            )}
            {commitment.button_url && L(commitment, "button_text") && (
              <a className={classes.textLink} href={commitment.button_url}>
                {L(commitment, "button_text")}
                <span aria-hidden="true"> →</span>
              </a>
            )}
          </div>

          <div className={classes.commitmentSide}>
            {commitment.image && (
              <img
                src={mediaUrl(commitment.image)}
                alt=""
                className={classes.commitmentImg}
              />
            )}
            {commitmentPoints.length > 0 && (
              <ul className={classes.pointList}>
                {commitmentPoints.map((p) => (
                  <li key={p.id} className={classes.pointItem}>
                    <span className={classes.pointMark} aria-hidden="true" />
                    {L(p, "text")}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* ---------------- SDG nima? ---------------- */}
      <section className={`${classes.section} ${classes.infoSection}`}>
        <h2 className={classes.infoTitle}>{infoTitle}</h2>
        <div className={classes.infoBox}>
          <p>{infoDescription}</p>
        </div>
      </section>

      {/* ---------------- 17 maqsad ---------------- */}
      {goals.length > 0 && (
        <section className={`${classes.section} ${classes.goalsSection}`}>
          <span className={classes.eyebrow}>{T("goalsEyebrow")}</span>
          <h2 className={classes.sectionTitle}>{goalsHeader}</h2>

          <div className={classes.goalsGrid}>
            {goals.map((goal) => {
              const goalTitle = L(goal, "title");
              const badgeColor = goal.color || SDG_COLORS[goal.number] || "#E5243B";
              const imageUrl = mediaUrl(goal.icon);

              return (
                <button
                  type="button"
                  key={goal.id || goal.number}
                  className={classes.goalCard}
                  style={{ "--goal-color": badgeColor }}
                  onClick={() => navigate(`/sdg/${goal.number || goal.slug}`)}
                >
                  <span className={classes.goalNo}>
                    {String(goal.number).padStart(2, "0")}-{T("goalLabel")}
                  </span>
                  <span className={classes.circleWrapper}>
                    <span
                      className={classes.circleBg}
                      style={{ backgroundColor: `${badgeColor}1F` }}
                    >
                      {imageUrl ? (
                        <img src={imageUrl} alt="" className={classes.circleImg} />
                      ) : (
                        getSDGIcon(goal.number, badgeColor)
                      )}
                    </span>
                    <span
                      className={classes.numberBadge}
                      style={{ backgroundColor: badgeColor }}
                    >
                      {goal.number}
                    </span>
                  </span>
                  <span className={classes.cardTitle}>{goalTitle}</span>
                  <span className={classes.cardArrow} aria-hidden="true">
                    ↗
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------------- Tashabbuslar ---------------- */}
      {initiatives.length > 0 && (
        <section className={`${classes.section} ${classes.initiatives}`}>
          <h2 className={classes.sectionTitle}>
            {lang === "ru"
              ? "Инициативы и практическая работа"
              : lang === "en"
                ? "Initiatives and practical work"
                : "Tashabbuslar va amaliy ishlar"}
          </h2>

          <div className={classes.initiativeGrid}>
            {initiatives.map((item) => {
              const img = mediaUrl(item.image);
              const linkText = L(item, "link_text");
              const Card = item.link_url ? "a" : "div";

              return (
                <Card
                  key={item.id}
                  className={classes.initiativeCard}
                  {...(item.link_url ? { href: item.link_url } : {})}
                >
                  <div className={classes.initiativeMedia}>
                    {img ? (
                      <img src={img} alt="" className={classes.initiativeImg} />
                    ) : (
                      <div className={classes.initiativePlaceholder} aria-hidden="true" />
                    )}
                  </div>
                  <div className={classes.initiativeBody}>
                    {L(item, "category") && (
                      <span className={classes.eyebrowSmall}>{L(item, "category")}</span>
                    )}
                    <h3 className={classes.initiativeTitle}>{L(item, "title")}</h3>
                    {L(item, "description") && (
                      <p className={classes.initiativeText}>{L(item, "description")}</p>
                    )}
                    {item.link_url && linkText && (
                      <span className={classes.textLink}>
                        {linkText}
                        <span aria-hidden="true"> ↗</span>
                      </span>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* ---------------- Strategiya ---------------- */}
      {strategy.length > 0 && (
        <section className={`${classes.section} ${classes.strategy}`}>
          <h2 className={classes.sectionTitle}>
            {lang === "ru"
              ? "Стратегия и направления"
              : lang === "en"
                ? "Strategy and direction"
                : "Strategiya va yo'nalish"}
          </h2>

          <div className={classes.strategyGrid}>
            {strategy.map((pillar, idx) => (
              <div key={pillar.id} className={classes.strategyItem}>
                <span className={classes.strategyNo}>
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className={classes.strategyTitle}>{L(pillar, "title")}</h3>
                {L(pillar, "description") && (
                  <p className={classes.strategyText}>{L(pillar, "description")}</p>
                )}
                {pillar.link_url && L(pillar, "link_text") && (
                  <a className={classes.textLink} href={pillar.link_url}>
                    {L(pillar, "link_text")}
                    <span aria-hidden="true"> ↗</span>
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ---------------- Manzil ---------------- */}
      {contact && (L(contact, "title") || L(contact, "address")) && (
        <section className={classes.contact}>
          <div className={classes.contactInner}>
            <div>
              {L(contact, "eyebrow") && (
                <span className={classes.eyebrowLight}>{L(contact, "eyebrow")}</span>
              )}
              {L(contact, "title") && (
                <h2 className={classes.contactTitle}>{L(contact, "title")}</h2>
              )}
              {L(contact, "note") && (
                <p className={classes.contactNote}>{L(contact, "note")}</p>
              )}
            </div>

            <div className={classes.contactRight}>
              {L(contact, "address") && (
                <address className={classes.contactAddress}>
                  {L(contact, "address")}
                </address>
              )}
              {contact.map_url && (
                <a
                  className={classes.contactLink}
                  href={contact.map_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {T("map")}
                  <span aria-hidden="true"> ↗</span>
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default SDGGoals;
